// hsl色空間とは
//   h: 色相(hue) 0-360 色味を0-360の範囲の角度で表す
//   s: 彩度(saturation) 0-100 100を純色として彩度が失われると灰色に近く
//   l: 輝度(luminance) 0-100 0を黒 100を白 中間である50を純色とする
// ekycでは輝度を調整することによりボタンの色を変化させるためにhsl色空間を用いる
// このコードでは値の範囲を0.0から1.0に変更している

// 値を範囲内に収める
function clamp(value: number, min: number, max: number): number {
  return value < min ? min : value > max ? max : value;
}

// rgb色空間からhsl色空間に変換する
// 値の範囲は全て0.0から1.0まで
function colorRgbToHsl(colorRgb: [number, number, number]): [number, number, number] {
  const r: number = clamp(colorRgb[0], 0.0, 1.0);
  const g: number = clamp(colorRgb[1], 0.0, 1.0);
  const b: number = clamp(colorRgb[2], 0.0, 1.0);
  const max: number = Math.max(r, g, b);
  const min: number = Math.min(r, g, b);
  let h: number = 0.0;
  let s: number = 0.0;
  let l: number = (max + min) / 2;
  if (max !== min) {
    if (max === r) h = ((60 * (g - b)) / (max - min) + 120 * 0) / 360;
    if (max === g) h = ((60 * (b - r)) / (max - min) + 120 * 1) / 360;
    if (max === b) h = ((60 * (r - g)) / (max - min) + 120 * 2) / 360;
    if (l <= 0.5) s = (max - min) / (max + min);
    else s = (max - min) / (2 - max - min);
  }
  h = h - Math.floor(h);
  s = clamp(s, 0.0, 1.0);
  l = clamp(l, 0.0, 1.0);
  return [h, s, l];
}

// hsl色空間からrgb色空間に変換する
// 値の範囲は全て0.0から1.0まで
function colorHslToRgb(colorHsl: [number, number, number]): [number, number, number] {
  const h360: number = (colorHsl[0] - Math.floor(colorHsl[0])) * 360;
  const s: number = clamp(colorHsl[1], 0.0, 1.0);
  const l: number = clamp(colorHsl[2], 0.0, 1.0);
  const max: number = l <= 0.5 ? l + l * s : l + (1.0 - l) * s;
  const min: number = l <= 0.5 ? l - l * s : l - (1.0 - l) * s;
  let r: number = 0.0;
  let g: number = 0.0;
  let b: number = 0.0;
  if (h360 < 60) {
    r = max;
    g = min + (max - min) * ((h360 - 0) / 60);
    b = min;
  } else if (60 <= h360 && h360 < 120) {
    r = min + (max - min) * ((120 - h360) / 60);
    g = max;
    b = min;
  } else if (120 <= h360 && h360 < 180) {
    r = min;
    g = max;
    b = min + (max - min) * ((h360 - 120) / 60);
  } else if (180 <= h360 && h360 < 240) {
    r = min;
    g = min + (max - min) * ((240 - h360) / 60);
    b = max;
  } else if (240 <= h360 && h360 < 300) {
    r = min + (max - min) * ((h360 - 240) / 60);
    g = min;
    b = max;
  } else {
    r = max;
    g = min;
    b = min + (max - min) * ((360 - h360) / 60);
  }
  r = clamp(r, 0.0, 1.0);
  g = clamp(g, 0.0, 1.0);
  b = clamp(b, 0.0, 1.0);
  return [r, g, b];
}

// 色文字列をhsl色空間に変換する
// シャープで始まるカラーコードのみ対応
export function colorStringToHsl(colorString: string): [number, number, number] {
  const m1: RegExpExecArray | null = /^#([0-9a-f][0-9a-f])([0-9a-f][0-9a-f])([0-9a-f][0-9a-f])$/i.exec(colorString);
  if (m1 !== null) {
    const r: number = parseInt(m1[1], 16) / 255;
    const g: number = parseInt(m1[2], 16) / 255;
    const b: number = parseInt(m1[3], 16) / 255;
    return colorRgbToHsl([r, g, b]);
  }

  const m2: RegExpExecArray | null = /^#([0-9a-f])([0-9a-f])([0-9a-f])$/i.exec(colorString);
  if (m2 !== null) {
    const r: number = parseInt(`${m2[1]}${m2[1]}`, 16) / 255;
    const g: number = parseInt(`${m2[2]}${m2[2]}`, 16) / 255;
    const b: number = parseInt(`${m2[3]}${m2[3]}`, 16) / 255;
    return colorRgbToHsl([r, g, b]);
  }

  return [0, 0, 0];
}

// hsl色空間から輝度を下げて色文字列に変換する
export function colorHslToStringWithLuminanceDecrease(colorHsl: [number, number, number], step: number): string {
  const h: number = colorHsl[0] - Math.floor(colorHsl[0]);
  const s: number = clamp(colorHsl[1], 0.0, 1.0);
  const l: number = clamp(colorHsl[2] - step * 0.05, 0.0, 1.0);
  const colorRgb: [number, number, number] = colorHslToRgb([h, s, l]);
  const r = clamp(colorRgb[0], 0.0, 1.0);
  const g = clamp(colorRgb[1], 0.0, 1.0);
  const b = clamp(colorRgb[2], 0.0, 1.0);
  const r16: string = `00${Math.floor(r * 255).toString(16)}`.substr(-2);
  const g16: string = `00${Math.floor(g * 255).toString(16)}`.substr(-2);
  const b16: string = `00${Math.floor(b * 255).toString(16)}`.substr(-2);
  return `#${r16}${g16}${b16}`;
}
